import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/app/common";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const isOpen = (p) => !["closed", "cancelled"].includes(p.status) && p.movement_status !== "fully_assigned";
export const canSubmitQc = (p) => isOpen(p) && p.status !== "draft" && !p.change_hold && ["not_submitted", "rejected"].includes(p.qc_status);

function PlanPicks({ plans, sel, setSel }) {
  const flip = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  return (
    <div className="space-y-2" data-testid="rfq-plan-list">
      {plans.map((p) => {
        const ok = canSubmitQc(p);
        return (
          <label key={p.id} className={`flex items-start gap-3 rounded border px-3 py-2 text-xs ${ok ? "cursor-pointer" : "opacity-60"} ${sel.includes(p.id) ? "border-sky-500 bg-sky-50" : "border-slate-200"}`}>
            <input type="checkbox" className="mt-0.5" disabled={!ok} checked={sel.includes(p.id)} onChange={() => flip(p.id)} data-testid={`rfq-pick-${p.planned_dispatch_number}`} />
            <span className="flex-1">
              <span className="flex flex-wrap items-center gap-2 font-semibold"><span className="font-mono">{p.planned_dispatch_number}</span><span className="font-normal text-slate-500">{fmtDate(p.expected_dispatch_date)}</span><StatusBadge status={p.qc_status} /><StatusBadge status={p.status} /></span>
              <span className="block text-slate-600">{p.item_summary}</span>
              {p.qc?.qc_remarks && <span className="block text-amber-800">Last QC remark: {p.qc.qc_remarks}</span>}
            </span>
          </label>
        );
      })}
    </div>
  );
}

// Production -> QC handoff: selected planned dispatches, or the full LM when it has no open plan
export function ReadyForQcDialog({ order, preselect = [], onClose, onDone }) {
  const { data } = useFetch(`/planned-dispatches?lm_id=${order.id}`);
  const [sel, setSel] = useState(preselect);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const open = data.filter(isOpen);
  const full = open.length === 0;
  const go = async () => {
    setBusy(true);
    try {
      const body = full ? { lm_id: order.id, qc_scope_type: "FullLM", confirm_full_lm: true, remarks } : { lm_id: order.id, qc_scope_type: "PlannedDispatchSelection", planned_dispatch_ids: sel, remarks };
      await api.post("/qc-submissions", body);
      toast.success(full ? `${order.order_no} (full LM) submitted as Ready for QC` : `${sel.length} planned dispatch(es) submitted as Ready for QC`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="ready-for-qc-dialog">
        <DialogHeader><DialogTitle>Ready for QC · {order.order_no}</DialogTitle></DialogHeader>
        {full
          ? <p className="rounded border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900" data-testid="rfq-full-lm-confirm">No planned dispatch exists. Is the full LM ready for dispatch?</p>
          : <><p className="text-xs text-slate-500">Tick the planned dispatches production has finished. QC must approve them before logistics can move them.</p><PlanPicks plans={open} sel={sel} setSel={setSel} /></>}
        <div><Label>Remarks for QC</Label><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="rfq-remarks" /></div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{full ? "No" : "Cancel"}</Button>
          <Button disabled={busy || (!full && !sel.length)} onClick={go} data-testid="rfq-submit-btn">{full ? "Yes, full LM is ready - submit to QC" : `Submit ${sel.length || ""} as Ready for QC`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
